import {
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { AgxButton, AgxCollapsible, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import { useCallback, useMemo, useState } from 'react';
import SpecialConditionClause from './components/SpecialConditionClause';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import { findSpecialConditionDueDate } from '../specialConditionDueDate';

interface SpecialConditionCollapsiblePanelProps {
  contractId: string;
  campaignId: string;
  renderedSpecialCondition: RenderedSpecialCondition;
  specialCondition: SpecialConditionData | undefined;
  specialConditions?: SpecialConditionData[];
  specialConditionIdToClauseNumberMap?: {
    [key: string]: string;
  };
  onDeleteCondition: (conditionId: string) => void;
  skipDeleteConfirmation?: boolean;
  deferDueDateSave?: boolean;
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => void | Promise<void>;
}

const SpecialConditionCollapsiblePanel = ({
  renderedSpecialCondition,
  specialCondition,
  onDeleteCondition,
  skipDeleteConfirmation = false,
  contractId,
  campaignId,
  onDueDateUpdated,
}: SpecialConditionCollapsiblePanelProps) => {
  const title = useMemo(
    () => `${renderedSpecialCondition.position} ${renderedSpecialCondition.title}`,
    [renderedSpecialCondition]
  );

  const onConditionDeleted = useCallback(() => {
    if (!renderedSpecialCondition?.id) return;

    onDeleteCondition(renderedSpecialCondition.id);
  }, [renderedSpecialCondition?.id, onDeleteCondition]);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  return (
    <AgxCollapsible
      key={renderedSpecialCondition.id}
      enableToggle
      open
      customTitle={
        <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
          <AgxTypography
            id={`special-condition-${renderedSpecialCondition.id}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {title}
          </AgxTypography>
        </AgxSpacer>
      }
      variant={'withDivider'}
      content={
        <AgxSpacer direction="vertical" gap={'md'}>
          {renderedSpecialCondition.clauses?.map((clause, index) => (
            <SpecialConditionClause
              clause={clause}
              key={clause.clauseId}
              isLastClause={index === (renderedSpecialCondition.clauses?.length ?? 0) - 1}
              conditionId={renderedSpecialCondition.id ?? ''}
              contractId={contractId}
              campaignId={campaignId}
              dueDateCondition={findSpecialConditionDueDate(specialCondition, clause.clauseId)}
              onDueDateUpdated={onDueDateUpdated}
            />
          ))}

          <AgxSpacer direction="horizontal" fill contentJustification="end">
            <AgxButton
              id={`delete-${renderedSpecialCondition.id}`}
              variant="link"
              danger
              text="Delete"
              onClick={() =>
                skipDeleteConfirmation ? onConditionDeleted() : setShowDeleteConfirmation(true)
              }
            />
            {!skipDeleteConfirmation && (
              <DeleteConfirmation
                name={`${renderedSpecialCondition.title} and all its clauses`}
                title="Really delete special condition?"
                lineOne="Are you sure you want to delete special condition"
                lineTwo="This cannot be undone."
                primaryActionTitle="Permanently delete special condition"
                secondaryActionTitle="No, cancel"
                showPopUp={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={onConditionDeleted}
              />
            )}
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  );
};

export default SpecialConditionCollapsiblePanel;
